import React, { Fragment, memo, useCallback } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import { KatoSelectTrigger, SelectOption } from '@app/components';
import { IconPlusCircle24 } from '@app/icons';
import { Company, Kato, UserPhone } from '@app/models';
import { fullName, formatPhoneNumber, getRawPhoneNumber } from '@app/helpers';

export interface CompanyInfoData {
  legalAddress: string;
  address: string;
  instagram: string;
  telegram: string;
  webSite: string;
  whatsAppNumber: string;
  phones: UserPhone[];
  kato: Kato | null;
  email: string;
  filesIds: string | null;
  avatarFilesIds: string | null;
}

interface Props {
  values: CompanyInfoData;
  errors: any;
  onChange: (value: any, name?: string) => void;
  company: Company;
}

function CompanyInfo(props: Props) {
  const { company } = props;
  const { values, onChange, errors } = props;

  const onChangeKato = useCallback(
    (data: SelectOption<Kato>) => {
      onChange(data.value, 'kato');
    },
    [onChange]
  );

  const onChangePhones = useCallback(
    (index: number) => (value: string) => {
      // Get only digits from the input
      const digitsOnly = getRawPhoneNumber(value);
      onChange(
        values.phones.map((prevPhone, phoneIndex) => ({
          ...prevPhone,
          phone: phoneIndex === index ? digitsOnly : prevPhone.phone,
        })),
        'phones'
      );
    },
    [onChange, values.phones]
  );

  const onClickAddPhone = useCallback(() => {
    onChange(
      [
        ...values.phones,
        {
          phone: '',
          isPrefer: false,
          isWhatsApp: false,
        },
      ],
      'phones'
    );
  }, [values.phones, onChange]);

  const onCheckPreferred = useCallback(
    (index: number) => () => {
      onChange(
        values.phones.map((prevPhone, phoneIndex) => ({
          ...prevPhone,
          isPrefer: phoneIndex === index,
        })),
        'phones'
      );
    },
    [onChange, values.phones]
  );

  const onChangeWhatsApp = useCallback(
    (value: string) => {
      // Get only digits from the input
      const digitsOnly = getRawPhoneNumber(value);
      onChange(digitsOnly, 'whatsAppNumber');
    },
    [onChange]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label
            htmlFor="company-name"
            className="block mb-1 text-sm font-medium"
          >
            наименование
          </label>
          <Input id="company-name" value={company!.name} disabled />
        </div>
        <div>
          <label
            htmlFor="company-bin"
            className="block mb-1 text-sm font-medium"
          >
            Бин
          </label>
          <Input id="company-bin" value={company!.bin} disabled />
        </div>
        <div>
          <label
            htmlFor="company-director"
            className="block mb-1 text-sm font-medium"
          >
            директор
          </label>
          <Input
            id="company-director"
            value={fullName(company!.owner)}
            disabled
          />
        </div>
        <div className="col-span-1 md:col-span-3">
          <KatoSelectTrigger
            label="Регион"
            placeholder="Выберите регион"
            value={(values.kato ? values.kato.nameRu : '') || undefined}
            onChange={onChangeKato}
            helperText={errors.kato || undefined}
            error={!!errors.kato}
          />
        </div>
        <div>
          <label
            htmlFor="legalAddress"
            className="block mb-1 text-sm font-medium"
          >
            Юридический адрес
          </label>
          <Input
            id="legalAddress"
            value={values.legalAddress || ''}
            placeholder="Введите юридический адрес"
            onChange={(e) => onChange(e.target.value, 'legalAddress')}
            name="legalAddress"
            required
          />
        </div>
        <div>
          <label htmlFor="address" className="block mb-1 text-sm font-medium">
            фактический адрес
          </label>
          <Input
            id="address"
            placeholder="Введите фактический адрес"
            value={values.address || ''}
            onChange={(e) => onChange(e.target.value, 'address')}
            name="address"
            required
          />
        </div>
      </div>
      <div className="space-y-4">
        {values.phones.map((phone, phoneIndex) => (
          <div
            key={phoneIndex}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center"
          >
            <div>
              <label
                htmlFor={`phone-${phoneIndex}`}
                className="block mb-1 text-sm font-medium"
              >
                Дополнительный номер телефона {phoneIndex + 1}
              </label>
              <Input
                id={`phone-${phoneIndex}`}
                onChange={(e) => onChangePhones(phoneIndex)(e.target.value)}
                value={formatPhoneNumber(phone.phone)}
                placeholder="+7 (777) 777 77 77"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id={`prefer-${phoneIndex}`}
                checked={phone.isPrefer}
                onCheckedChange={onCheckPreferred(phoneIndex)}
              />
              <label htmlFor={`prefer-${phoneIndex}`} className="text-sm">
                Предпочитаемый способ связи
              </label>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          className="mt-2 flex items-center gap-2"
          onClick={onClickAddPhone}
        >
          <IconPlusCircle24 /> Добавить дополнительный номер
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="email" className="block mb-1 text-sm font-medium">
            E-mail
          </label>
          <Input
            id="email"
            onChange={(e) => onChange(e.target.value, 'email')}
            name="email"
            value={values.email}
            placeholder="Введите e-mail"
            required
          />
        </div>
        <div>
          <label htmlFor="webSite" className="block mb-1 text-sm font-medium">
            Адрес сайта
          </label>
          <Input
            id="webSite"
            onChange={(e) => onChange(e.target.value, 'webSite')}
            name="webSite"
            value={values.webSite}
            placeholder="Введите сайт"
          />
        </div>
        <div>
          <label
            htmlFor="whatsAppNumber"
            className="block mb-1 text-sm font-medium"
          >
            Номер WhatsApp
          </label>
          <Input
            id="whatsAppNumber"
            onChange={(e) => onChangeWhatsApp(e.target.value)}
            name="whatsAppNumber"
            value={formatPhoneNumber(values.whatsAppNumber)}
            placeholder="+7 (777) 777 77 77"
          />
        </div>
        <div>
          <label htmlFor="instagram" className="block mb-1 text-sm font-medium">
            Instagram
          </label>
          <Input
            id="instagram"
            onChange={(e) => onChange(e.target.value, 'instagram')}
            name="instagram"
            value={values.instagram}
            placeholder="Введите instagram"
          />
        </div>
        <div>
          <label htmlFor="telegram" className="block mb-1 text-sm font-medium">
            Telegram
          </label>
          <Input
            id="telegram"
            onChange={(e) => onChange(e.target.value, 'telegram')}
            name="telegram"
            value={values.telegram}
            placeholder="Введите telegram"
          />
        </div>
      </div>
    </div>
  );
}

export default memo(CompanyInfo);
